import React, { useState } from "react";
import {
 Box,
 Card,
 Grid,
 Group,
 Text,
 Title,
 Tooltip,
 Paper,
 Button,
 useMantineTheme,
} from "@mantine/core";
import { calculateEngagement } from "../../../utils/calculateEngagement";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

export default function SessionCalendarHeatmap({
 sessions,
 clientName,
 plannedFrequencyPerMonth,
}) {
 const frequencyNum = Number(plannedFrequencyPerMonth) || 0;
 const theme = useMantineTheme();

 // Prepare date array & sorted sessions
 dayjs.extend(utc);
const sessionDates = sessions?.map(({ timestamp }) =>
  dayjs.utc(timestamp, 'YYYY-MM-DD').startOf('day')
) || [];
 const sortedDates = [...sessionDates].sort((a, b) => a.valueOf() - b.valueOf());

 const startYear = sortedDates?.[0]?.isValid?.() ? sortedDates[0].year() : dayjs().year();
 const endYear = sortedDates[sortedDates.length - 1]?.year() ?? startYear;
 const [year, setYear] = useState(startYear);

if (!sortedDates.length) {
  return (
    <Card withBorder radius="md" p="md" mt="md" shadow="xs">
      <Title color="gray" order={5} tt="uppercase">Session Calendar</Title>
      <Title color="gray" order={6} style={{ marginTop: 12 }}>No sessions available</Title>
    </Card>
  );
}

 // Build date → count map
 const sessionMap = new Map();
 sessionDates.forEach((date) => {
   const d = date.format("YYYY-MM-DD");
   sessionMap.set(d, (sessionMap.get(d) || 0) + 1);
 });

 // Build months grouped per year (INCLUDING empty months between first and last session)
const byYear = {};

if (sortedDates.length > 0) {
  const firstDate = sortedDates[0].startOf("month");
  const lastDate = sortedDates[sortedDates.length - 1].startOf("month");

  let current = firstDate.clone();
  const end = lastDate.clone();

  while (current.isSame(end) || current.isBefore(end)) {
    const y = current.year();
    const m = current.month();
    const first = current.startOf("month");
    const last = current.endOf("month");

    const daysArr = Array.from({ length: last.date() }, (_, i) => {
  const dt = dayjs.utc(`${y}-${String(m + 1).padStart(2, "0")}-${String(i + 1).padStart(2, "0")}`, 'YYYY-MM-DD');
  if (!dt.isValid()) return null;
  const key = dt.format("YYYY-MM-DD");
  return {
    date: dt,
    count: sessionMap.get(key) || 0,
  };
}).filter((d) => d?.date?.isValid?.())

    byYear[`${y}-${m}`] = {
      year: y,
      monthName: current.format("MMM").toUpperCase(),
      offset: (first.day() + 6) % 7,
      days: daysArr,
    };

    current = current.add(1, "month");
  }
}

 // Filter & sort months for current year
 const months = Object.values(byYear)
   .filter((m) => m.year === year)
   .sort((a, b) => {
     const monthOrder = { JAN: 0, FEB: 1, MAR: 2, APR: 3, MAY: 4, JUN: 5, JUL: 6, AUG: 7, SEP: 8, OCT: 9, NOV: 10, DEC: 11 };
     return monthOrder[a.monthName] - monthOrder[b.monthName];
   });

 // Engagement calculation
 const { avgGap, expectedGap } = calculateEngagement({
   sessions,
   actions: [],
   topics: [],
   sessionFrequency: plannedFrequencyPerMonth,
 });

 // Navigation
 const availableYears = Array.from(new Set(Object.values(byYear).map(m => m.year))).sort();

 const currentIndex = availableYears.indexOf(year);
 const prevYear = availableYears[currentIndex - 1];
 const nextYear = availableYears[currentIndex + 1];

 // Round
 const actualGapRounded = Math.round(avgGap);
 const expectedGapRounded = Math.round(expectedGap);

 const colorScale = {
   0: theme.colors.gray[2],
   1: theme.colors.blue[2],
   2: theme.colors.blue[4],
   3: theme.colors.blue[6],
   4: theme.colors.blue[8],
 };


 return (
    <Paper h={'100%'} p="md" shadow="lg" withBorder>
     <Group position="apart" mb="md">
       <Title color="gray" order={5} tt="uppercase">Session Calendar — {year}</Title>
       <Group spacing="xs">
         <Button onClick={() => setYear(prevYear)} disabled={!prevYear} variant="filled" color="darkred">
           ◀ Previous
         </Button>
         <Button onClick={() => setYear(nextYear)} disabled={!nextYear} variant="filled" color="darkred">
           Next ▶
         </Button>
       </Group>
     </Group>

     <Group spacing="xl" mb="md">
       <Title order={5} fw={500} color="gray.6">
         Total sessions: <strong>{sessions.length}</strong>
       </Title>
       <Title order={5} fw={500} color="gray.6">
         Actual gap: <strong>{actualGapRounded} days</strong>, Planned gap:{" "}
         <strong>{expectedGapRounded} days</strong>
       </Title>
     </Group>

     <Grid gutter="md">
       {months.map((mono, idx) => (
     <Grid.Col
     span={4}   // Default: 3 columns even on smallest screens
     sm={3}     
     md={3}     
     lg={3}    
     xl={2}     // 6 columns from 1200px+
     key={idx}
   >
           <Text weight={600} size="sm" mb="4px">
             {mono.monthName}
           </Text>
           <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 2 }}>
             {Array.from({ length: mono.offset }).map((_, i) => (
               <Box key={`off-${i}`}>&nbsp;</Box>
             ))}
             {mono.days.map(({ date, count }, index) => {
              const isValid = date.isValid();
              const formatted = isValid ? date.local().format("DD MMM YYYY") : "Invalid Date";
              const cellKey = date.isValid()
              ? date.format("YYYY-MM-DD")
              : `invalid-${mono.year}-${mono.monthName}-${index}`;

              return (
                <Tooltip
                  key={cellKey}
                  label={`${formatted}: ${count} session${count !== 1 ? 's' : ''}`}
                  disabled={count === 0}
                  position="top"
                  withArrow
                >
                  <Box
                    sx={{
                      backgroundColor: colorScale[Math.min(count, 4)],
                      aspectRatio: "1",
                      borderRadius: 4,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                      fontWeight: 600,
                      color: count > 0 ? "white" : theme.colors.gray[6],
                      cursor: count > 0 ? "pointer" : "default",
                    }}
                  >
                    {isValid ? date.date() : "-"}
                  </Box>
                </Tooltip>
              );
            })}
           </Box>
         </Grid.Col>
       ))}
     </Grid>
   </Paper>
 );
}
